import { useQueries, useQuery } from '@tanstack/react-query';
import { blocksClient } from '@/lib/blocks/client';
import {
  getOrganizationTimeZone,
  isValidTimeZone,
  setOrganizationTimeZone,
} from '@/lib/utils/custom-date/org-time-zone';
import { useAuthStore } from '@/state/store/auth';

export interface OrganizationOption {
  itemId: string;
  name: string;
  /**
   * IANA timezone from the organization's own IAM record (e.g. `Asia/Dhaka`). Kept in IAM rather than a
   * code constant because org ids differ per environment. Absent when the org has no readable record.
   */
  timeZone?: string;
  /** Role slugs the signed-in admin themselves holds in this organization — they can only grant these. */
  roles: string[];
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/**
 * The organization ids the signed-in admin's own IAM record grants access to. `iam.organizations.list`
 * (the tenant-wide admin browse endpoint) was tried first, but it reports a `totalCount` while its item
 * array comes back empty on this tenant, and real IAM user records here carry `organizationIds` +
 * `OrganizationsRoles` (an `{orgId: roles[]}` map, confirmed against a live user record), not the
 * SDK-typed `memberships` array — both shapes, plus the singular `organizationId`, are read defensively
 * so this keeps working whichever one a given account actually returns.
 */
const ownOrganizationIds = (me: Record<string, unknown>): string[] => {
  const ids = new Set<string>();
  asStringArray(me.organizationIds).forEach((id) => ids.add(id));
  if (typeof me.organizationId === 'string' && me.organizationId) ids.add(me.organizationId);
  Object.keys(asRecord(me.OrganizationsRoles)).forEach((id) => ids.add(id));
  const memberships = Array.isArray(me.memberships)
    ? (me.memberships as Record<string, unknown>[])
    : [];
  memberships.forEach((membership) => {
    if (typeof membership.organizationId === 'string' && membership.organizationId) {
      ids.add(membership.organizationId);
    }
  });
  return [...ids];
};

const DEFAULT_ORGANIZATION_LABEL = 'Default';

/**
 * Best-effort display name and timezone for an organization id; not every id has a queryable
 * Organization record. The SDK types this response as `{ data: {...} }`, but the live
 * `GET /iam/v4/iam/organizations/{id}` endpoint actually replies with `{ organization: {...} }`
 * (confirmed against a live tenant) — both keys are read defensively so this keeps working if either
 * shape is what actually comes back.
 */
const resolveOrganization = async (
  organizationId: string
): Promise<{ name: string; timeZone?: string }> => {
  // The built-in default organization has no IAM record (Blocks rejects updates to it), so its time zone
  // is configured per environment instead.
  if (organizationId === 'default') {
    return {
      name: DEFAULT_ORGANIZATION_LABEL,
      timeZone: import.meta.env.VITE_DEFAULT_ORGANIZATION_TIME_ZONE || undefined,
    };
  }
  try {
    const response = (await blocksClient.iam.organizations.get(
      organizationId
    )) as unknown as Record<string, unknown>;
    const organization = asRecord(response.data ?? response.organization);
    const { name, timeZone } = organization;
    return {
      name: typeof name === 'string' && name ? name : organizationId,
      timeZone: typeof timeZone === 'string' && timeZone ? timeZone : undefined,
    };
  } catch {
    return { name: organizationId };
  }
};

const resolveOrganizationName = async (organizationId: string): Promise<string> =>
  (await resolveOrganization(organizationId)).name;

/**
 * Every organization the signed-in admin themselves has access to, so they can grant it to someone else.
 * `iam.me()` alone was tried first, but its own `organizationId`/`organizationIds` fields did not include
 * an organization the admin is a real member of — `iam.me()` most likely reflects the access token's own
 * claims rather than a fresh read, and those can lag a grant made since the token was last issued. Reading
 * back through `users.get(myItemId)` (the same reliably-live path used for any other user's access) avoids
 * that staleness.
 */
export const useOrganizations = () =>
  useQuery({
    queryKey: ['iam', 'organizations', 'mine'],
    queryFn: async (): Promise<OrganizationOption[]> => {
      const me = asRecord((await blocksClient.iam.me()).data);
      const myItemId = typeof me.itemId === 'string' ? me.itemId : undefined;
      const record = myItemId ? asRecord((await blocksClient.iam.users.get(myItemId)).data) : me;
      const organizationIds = ownOrganizationIds(record);
      const organizationsRoles = asRecord(record.OrganizationsRoles);
      const flatRoles = asStringArray(record.roles);
      return Promise.all(
        organizationIds.map(async (itemId) => ({
          itemId,
          ...(await resolveOrganization(itemId)),
          // Falls back to the flat `roles` list when there's no per-organization map for this account
          // shape (see `getIamUserOrganizationAccess` for the same fallback on the read side).
          roles: asStringArray(organizationsRoles[itemId]).length
            ? asStringArray(organizationsRoles[itemId])
            : flatRoles,
        }))
      );
    },
    staleTime: 5 * 60 * 1000,
  });

/**
 * Best-effort display names for organization ids that aren't already known locally — e.g. an
 * organization an employee has access to that the viewer/admin doesn't themselves belong to. Falls
 * back to the id itself while a lookup is pending or if it fails, so callers never need to.
 */
export const useOrganizationNames = (organizationIds: string[]): Record<string, string> =>
  useOrganizationNamesWithStatus(organizationIds).names;

/** `useOrganizationNames`, plus whether any name is still loading (its id stands in meanwhile). */
export const useOrganizationNamesWithStatus = (organizationIds: string[]) => {
  const queries = useQueries({
    queries: organizationIds.map((organizationId) => ({
      queryKey: ['iam', 'organizations', 'get', organizationId],
      queryFn: () => resolveOrganizationName(organizationId),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const names: Record<string, string> = Object.fromEntries(
    organizationIds.map((id, index) => [id, queries[index]?.data ?? id])
  );
  return { names, isLoading: queries.some((query) => query.isLoading) };
};

/**
 * Display name of the organization the user is signed in to (`'Default'` for the built-in one, the
 * id itself when its record can't be read). `data` is undefined until it has resolved.
 */
export const useCurrentOrganizationName = () => {
  const organizationId = useAuthStore((state) => state.selectedOrgId) ?? undefined;
  return useQuery({
    queryKey: ['iam', 'organizations', 'get', organizationId],
    queryFn: () => resolveOrganizationName(organizationId as string),
    enabled: Boolean(organizationId),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Resolves the time zone of the organization the user is signed in to and makes it the zone every picked
 * calendar day is read and stored in (see `org-time-zone.ts`). `isMissing` is set when it has none that
 * can be read (no access to the organization record, or none set — for the built-in default organization,
 * `VITE_DEFAULT_ORGANIZATION_TIME_ZONE`); the app is then not usable in that organization.
 */
export const useOrganizationTimeZone = () => {
  const organizationId = useAuthStore((state) => state.selectedOrgId) ?? undefined;

  const { data, isPending } = useQuery({
    queryKey: ['iam', 'organizations', 'get', organizationId, 'time-zone'],
    queryFn: async () => (await resolveOrganization(organizationId as string)).timeZone ?? null,
    enabled: Boolean(organizationId),
    staleTime: Infinity,
  });

  // Set while rendering (not in an effect) so the pages rendered below already read and write in it.
  setOrganizationTimeZone(data);

  const isResolving = Boolean(organizationId) && isPending;

  return {
    timeZone: getOrganizationTimeZone(),
    isResolving,
    isMissing: Boolean(organizationId) && !isResolving && !isValidTimeZone(data),
  };
};
